import { useCallback, useEffect, useRef } from 'react';

import { Timeout } from 'src/types';

export interface UseTimerParams {
  idleMs?: number;
  onPause: () => void;
  onResume: () => void;
  when?: boolean;
}

export const useIdleTimer = ({
  idleMs,
  onPause,
  onResume,
  when = true,
}: UseTimerParams): void => {
  const idleTimeoutId = useRef<Timeout | null>(null);
  const running = useRef(true);
  const subscribed = useRef(false);

  const pause = useCallback(() => {
    if (!running.current) return;

    running.current = false;

    if (idleTimeoutId.current) clearTimeout(idleTimeoutId.current);

    onPause();
  }, [onPause]);

  const resume = useCallback(() => {
    if (running.current) return;

    running.current = true;

    onResume();
  }, [onResume]);

  const resetIdleTimer = useCallback(() => {
    resume();

    if (idleTimeoutId.current) clearTimeout(idleTimeoutId.current);

    idleTimeoutId.current = setTimeout(pause, idleMs);
  }, [idleMs, pause, resume]);

  const subscribe = useCallback(() => {
    window.addEventListener('focus', resetIdleTimer);
    window.addEventListener('blur', pause);
    document.addEventListener('mousemove', resetIdleTimer);
    document.addEventListener('mousedown', resetIdleTimer);
    document.addEventListener('keypress', resetIdleTimer);
    document.addEventListener('scroll', resetIdleTimer);

    subscribed.current = true;
  }, [pause, resetIdleTimer]);

  const unsubscribe = useCallback(() => {
    window.removeEventListener('focus', resetIdleTimer);
    window.removeEventListener('blur', pause);
    document.removeEventListener('mousemove', resetIdleTimer);
    document.removeEventListener('mousedown', resetIdleTimer);
    document.removeEventListener('keypress', resetIdleTimer);
    document.removeEventListener('scroll', resetIdleTimer);

    pause();

    subscribed.current = false;
  }, [pause, resetIdleTimer]);

  useEffect(() => {
    if (when && !subscribed.current) {
      subscribe();
    }

    if (!when && subscribed.current) {
      unsubscribe();
    }

    return () => {
      if (subscribed.current) unsubscribe();
    };
  }, [subscribe, unsubscribe, when]);
};
